import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { useBoardUsers } from "../../../../../../../reactQuery/users";
import { updateComment } from "../../../../../../../services/apiComment";
import { useResponsive } from "../../../../../../../hooks/useResponsive";
import useMutate from "../../../../../../../hooks/useMutate";
import useBoardIdByKey from "../../../../../../../hooks/useBoardIdByKey";
import { getResponseData } from "../../../../../../../reactQuery/response";
import {
  getEditorCommentValue,
  getTaggedUserIds,
} from "../../hooks/commentUtils";

export function useDetailEditCommentState({
  cardId,
  commentId,
  initialComment,
  setShowEdit,
}) {
  const [comment, setComment] = useState(initialComment);
  const [mentionListVisible, setMentionListVisible] = useState(false);
  const [elementPos, setElementPos] = useState();
  const [cursorPosition, setCursorPosition] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState([]);
  const [sortUser, setSortUser] = useState("");
  const [isDisable, setIsDisable] = useState(true);
  const [selectedUserForTag, setSelectedUserForTag] = useState([]);
  const editorRef = useRef(null);
  const queryClient = useQueryClient();
  const isMobile = useResponsive("down", "md");
  const { id } = useBoardIdByKey();
  const [searchParams] = useSearchParams();
  const { data } = useBoardUsers(searchParams.get("brd_id") || id);
  const users = useMemo(() => getResponseData(data, []), [data]);

  useEffect(() => {
    setSelectedUserForTag(users);
  }, [users]);

  const { mutate: updateCommentMutate } = useMutate(
    updateComment,
    [],
    () => {},
    () => {
      queryClient.invalidateQueries({
        queryKey: ["comment", cardId],
      });
      queryClient.invalidateQueries({
        queryKey: ["history", String(cardId)],
      });
    },
    () => {}
  );

  const handleSave = useCallback(() => {
    if (!comment || isDisable) {
      return;
    }

    updateCommentMutate({
      comment_id: commentId,
      comment,
      tagged_user: getTaggedUserIds(selectedId, users, comment).join(",") || null,
    });
    setShowEdit(false);
  }, [
    cardId,
    comment,
    commentId,
    isDisable,
    selectedId,
    setShowEdit,
    updateCommentMutate,
    users,
  ]);

  const handleMention = useCallback((cursorPos) => {
    const nextAnchorEl = document.getElementById("forAnchor1");
    const nextRect = nextAnchorEl?.getBoundingClientRect();

    setElementPos({
      top: nextRect?.top,
      left: nextRect?.left,
    });
    setCursorPosition(cursorPos - 1);
    setAnchorEl(nextAnchorEl);
    setMentionListVisible(true);
  }, []);

  const handleUserSelect = useCallback(
    (userData) => {
      const quillEditor = editorRef?.current?.getEditor();
      setSelectedId((previousIds) => [...previousIds, userData?.user_id]);

      quillEditor.deleteText(cursorPosition, 1);
      quillEditor.insertText(cursorPosition, `@${userData?.username} `);
      quillEditor.setSelection(cursorPosition + userData?.username?.length + 1);
      setMentionListVisible(false);
    },
    [cursorPosition]
  );

  const handleComment = useCallback(() => {
    const commentData = getEditorCommentValue(editorRef);

    if (!commentData) {
      setIsDisable(true);
      setComment(initialComment);
      return;
    }

    setComment(editorRef?.current?.value);
    setIsDisable(false);
  }, [initialComment]);

  const handleChange = useCallback(
    (event) => {
      const searchedText = event?.target?.value?.trim()?.toLowerCase();
      setSortUser(searchedText || "");
      setSelectedUserForTag(
        users.filter((userData) =>
          userData?.username?.toLowerCase()?.includes(searchedText)
        )
      );
    },
    [users]
  );

  const handleMentionClose = useCallback(() => {
    setSortUser("");
    setMentionListVisible(false);
  }, []);

  const handleCancel = useCallback(() => {
    setShowEdit(false);
  }, [setShowEdit]);

  return {
    anchorEl,
    comment,
    editorRef,
    elementPos,
    handleCancel,
    handleChange,
    handleComment,
    handleMention,
    handleMentionClose,
    handleSave,
    handleUserSelect,
    isDisable,
    isMobile,
    mentionListVisible,
    selectedUserForTag,
    setElementPos,
    sortUser,
  };
}
